import { useState, useEffect, useRef, useCallback } from 'react';

type Callback = () => void;

type UseDebounceReturn<T> = [
  T, // debounced value
  (val: T, callback?: Callback) => void, // set value (debounced)
  (val: T) => void, // set value (immediate)
];

/**
 * Hook for debouncing a value with optional callback support.
 *
 * @param initialValue - Initial value
 * @param delay - Debounce delay in milliseconds
 * @returns [debouncedValue, debouncedSetValue, immediateSetValue]
 *
 * @example
 * const [searchTerm, setSearchTerm, setImmediateSearchTerm] = useDebounce('', 300);
 *
 * // Debounced update
 * setSearchTerm(e.target.value, () => console.log('Debounce complete'));
 *
 * // Immediate update (bypass debounce)
 * setImmediateSearchTerm('reset');
 */
export function useDebounce<T>(initialValue: T, delay: number): UseDebounceReturn<T> {
  const [value, setValue] = useState<T>(initialValue);
  const [debouncedValue, setDebouncedValue] = useState<T>(initialValue);
  const callbackRef = useRef<Callback | undefined>(undefined);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
      if (callbackRef.current) {
        callbackRef.current();
        callbackRef.current = undefined;
      }
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  const debouncedSetValue = useCallback((val: T, callback?: Callback) => {
    setValue(val);
    callbackRef.current = callback;
  }, []);

  const immediateSetValue = useCallback((val: T) => {
    setValue(val);
    setDebouncedValue(val);
    callbackRef.current = undefined;
  }, []);

  return [debouncedValue, debouncedSetValue, immediateSetValue];
}

/**
 * Simplified debounce hook that just returns the debounced value.
 *
 * @example
 * const [searchTerm, setSearchTerm] = useState('');
 * const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
 */
export function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
